import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useTenant } from "../lib/tenant-context";

interface KnowledgeStats {
  total: number;
  indexed: number;
  pending: number;
  errors: number;
  chunks: number;
  countsByType: Record<string, number>;
  countsByStatus: Record<string, number>;
  categories: string[];
  recentItems: Array<{
    id: string;
    type: string;
    title: string;
    embedding_status: string;
    updated_at: string;
  }>;
  healthScore: number;
}

interface KnowledgeItem {
  id: string;
  type: string;
  category: string;
  title: string;
  summary: string;
  source_type: string;
  embedding_status: string;
  chunk_count: number;
  is_published: number;
  updated_at: string;
  tags: string[];
  metadata?: {
    image_url?: string;
    [key: string]: unknown;
  };
}

const KNOWLEDGE_TYPES = [
  {
    id: "product",
    label: "Products",
    description: "Product catalog from connected stores",
    icon: "PRD",
    syncable: true,
  },
  {
    id: "material",
    label: "Materials",
    description: "Fabric technologies, care instructions",
    icon: "M",
  },
  {
    id: "policy",
    label: "Policies",
    description: "Return, trade-in, warranty rules",
    icon: "P",
  },
  {
    id: "faq",
    label: "FAQs",
    description: "Common questions and answers",
    icon: "Q",
  },
  {
    id: "training",
    label: "Training",
    description: "Uploaded documents, guides",
    icon: "T",
  },
  {
    id: "custom",
    label: "Custom",
    description: "Other knowledge topics",
    icon: "C",
  },
];

export const Route = createFileRoute("/knowledge")({
  component: KnowledgeHubPage,
});

function KnowledgeHubPage() {
  const { currentTenant } = useTenant();
  const [stats, setStats] = useState<KnowledgeStats | null>(null);
  const [items, setItems] = useState<KnowledgeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadType, setUploadType] = useState("training");
  const [uploadCategory, setUploadCategory] = useState("");
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadData();
  }, [activeType, searchQuery, currentTenant.id]);

  // Reload when tenant changes
  useEffect(() => {
    const handleTenantChange = () => {
      loadData();
    };
    window.addEventListener("tenantChanged", handleTenantChange);
    return () => {
      window.removeEventListener("tenantChanged", handleTenantChange);
    };
  }, []);

  const syncProducts = async () => {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const selectedTenantId =
        localStorage.getItem("selectedTenantId") || "lululemon";
      const res = await fetch("/api/v1/knowledge/sync-products", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId: selectedTenantId }),
      });
      const data = await res.json();
      if (data.success) {
        setSyncMessage(
          `Synced ${data.synced} new, updated ${data.updated} products`,
        );
        loadData(); // Reload to show new items
      } else {
        setSyncMessage(data.message || data.error || "Sync failed");
      }
    } catch (error) {
      setSyncMessage("Failed to sync products");
      console.error(error);
    } finally {
      setSyncing(false);
      // Clear message after 5 seconds
      setTimeout(() => setSyncMessage(null), 5000);
    }
  };

  const handleFileUpload = async () => {
    if (!uploadFile) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append(
        "title",
        uploadTitle || uploadFile.name.replace(/\.[^/.]+$/, ""),
      );
      formData.append("type", uploadType);
      formData.append("category", uploadCategory || "Uploads");

      const res = await fetch("/api/v1/knowledge/upload", {
        method: "POST",
        body: formData,
      });

      const data = await res.json();
      if (data.success) {
        setSyncMessage(`Uploaded: ${data.title}. Ready to index.`);
        setShowUploadModal(false);
        setUploadFile(null);
        setUploadTitle("");
        setUploadCategory("");
        loadData();
      } else {
        setSyncMessage(data.error || "Upload failed");
      }
    } catch (error) {
      console.error("Upload failed:", error);
      setSyncMessage("Upload failed");
    } finally {
      setUploading(false);
      setTimeout(() => setSyncMessage(null), 5000);
    }
  };

  const [indexing, setIndexing] = useState(false);
  const indexAll = async () => {
    setIndexing(true);
    setSyncMessage(null);
    try {
      const res = await fetch("/api/v1/knowledge/index-all", {
        method: "POST",
      });
      const data = await res.json();
      if (data.success) {
        setSyncMessage(`Indexed ${data.indexed} items`);
        loadData();
      } else {
        setSyncMessage(data.error || "Indexing failed");
      }
    } catch (error) {
      setSyncMessage("Failed to index");
      console.error(error);
    } finally {
      setIndexing(false);
      setTimeout(() => setSyncMessage(null), 5000);
    }
  };

  const loadData = async () => {
    try {
      // Get selected tenant from localStorage
      const selectedTenantId =
        typeof window !== "undefined"
          ? localStorage.getItem("selectedTenantId") || "lululemon"
          : "lululemon";

      // Load stats
      const statsRes = await fetch(
        `/api/v1/knowledge/stats?tenantId=${selectedTenantId}`,
      );
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      } else if (currentTenant.id === "arcteryx") {
        // Use demo stats for Arc'teryx
        setStats(getArcteryxDemoStats());
      }

      // Load items with filters
      let url = `/api/v1/knowledge?tenantId=${selectedTenantId}&limit=50`;
      if (activeType) url += `&type=${activeType}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;

      const itemsRes = await fetch(url);
      if (itemsRes.ok) {
        const data = await itemsRes.json();
        setItems(data.items || []);
      } else {
        // Use tenant-specific demo items
        let demoItems = getTenantDemoKnowledge(currentTenant.id);
        if (activeType) {
          demoItems = demoItems.filter((item) => item.type === activeType);
        }
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          demoItems = demoItems.filter(
            (item) =>
              item.title.toLowerCase().includes(query) ||
              item.summary.toLowerCase().includes(query),
          );
        }
        setItems(demoItems);
      }
    } catch (error) {
      console.error("Failed to load knowledge:", error);
      // Fallback to tenant-specific demo data on error
      setStats(getTenantDemoStats(currentTenant.id));
      let demoItems = getTenantDemoKnowledge(currentTenant.id);
      if (activeType) {
        demoItems = demoItems.filter((item) => item.type === activeType);
      }
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        demoItems = demoItems.filter(
          (item) =>
            item.title.toLowerCase().includes(query) ||
            item.summary.toLowerCase().includes(query),
        );
      }
      setItems(demoItems);
    } finally {
      setLoading(false);
    }
  };

  // Reload when tenant changes
  useEffect(() => {
    const handleStorageChange = () => {
      loadData();
    };
    window.addEventListener("storage", handleStorageChange);
    // Also listen for custom event from tenant switcher
    window.addEventListener("tenantChanged", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("tenantChanged", handleStorageChange);
    };
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "indexed":
        return "#00FBA9";
      case "pending":
        return "#FFB800";
      case "processing":
        return "#3941EB";
      case "error":
        return "#FF4C00";
      default:
        return "#A9A9A9";
    }
  };

  const getTypeIcon = (type: string) => {
    const found = KNOWLEDGE_TYPES.find((t) => t.id === type);
    return found?.icon || type[0].toUpperCase();
  };

  const handleSelectItem = (itemId: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedItems.size === items.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(items.map((item) => item.id)));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.size === 0) return;

    if (
      !confirm(`Delete ${selectedItems.size} item(s)? This cannot be undone.`)
    ) {
      return;
    }

    setDeleting(true);
    try {
      const response = await fetch("/api/v1/knowledge/batch-delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: Array.from(selectedItems) }),
      });

      if (!response.ok) {
        throw new Error("Failed to delete items");
      }

      setSelectedItems(new Set());
      await loadData();
    } catch (error) {
      console.error("Failed to delete items:", error);
      alert("Failed to delete items. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-6xl">
      {/* Header */}
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-2xl font-light mb-2" style={{ color: "#171717" }}>
            Knowledge Hub
          </h1>
          <p className="text-sm" style={{ color: "#A9A9A9" }}>
            Manage what your AI Educator knows
          </p>
        </div>
        <div className="flex gap-3">
          {stats && stats.pending > 0 && (
            <button
              onClick={indexAll}
              disabled={indexing}
              className="px-4 py-2 rounded-full text-sm font-medium border"
              style={{
                borderColor: indexing ? "#E5E5E5" : "#FFB800",
                color: indexing ? "#A9A9A9" : "#FFB800",
                backgroundColor: "transparent",
              }}
            >
              {indexing ? "Indexing..." : `Index ${stats.pending} Pending`}
            </button>
          )}
          <button
            onClick={() => setShowUploadModal(true)}
            className="px-4 py-2 rounded-full text-sm font-medium border"
            style={{ borderColor: "#3941EB", color: "#3941EB" }}
          >
            Upload File
          </button>
          <Link
            to="/knowledge/new"
            className="px-4 py-2 rounded-full text-sm font-medium"
            style={{ backgroundColor: "#00FBA9", color: "#171717" }}
          >
            Add Knowledge
          </Link>
        </div>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-8">
          <StatCard
            label="Total Items"
            value={stats.total}
            subtext={`${stats.chunks} chunks indexed`}
          />
          <StatCard
            label="Indexed"
            value={stats.indexed}
            subtext={`${stats.healthScore}% ready`}
            color="#00FBA9"
          />
          <StatCard
            label="Pending"
            value={stats.pending}
            subtext="Waiting to index"
            color="#FFB800"
          />
          <StatCard
            label="Errors"
            value={stats.errors}
            subtext={stats.errors > 0 ? "Needs attention" : "All good"}
            color={stats.errors > 0 ? "#FF4C00" : "#A9A9A9"}
          />
        </div>
      )}

      {/* Knowledge Type Cards */}
      <div className="mb-8">
        <h2
          className="text-xs font-medium tracking-wider mb-4"
          style={{ color: "#A9A9A9" }}
        >
          KNOWLEDGE BY TYPE
        </h2>
        <div className="grid grid-cols-6 gap-4">
          {KNOWLEDGE_TYPES.map((type) => {
            const count = stats?.countsByType[type.id] || 0;
            const isActive = activeType === type.id;
            const isSyncable = "syncable" in type && type.syncable;

            return (
              <div
                key={type.id}
                className="bg-white border p-4 text-left transition-all hover:shadow-md relative"
                style={{
                  borderColor: isActive ? "#00FBA9" : "rgba(169,169,169,0.2)",
                  borderWidth: isActive ? "2px" : "1px",
                }}
              >
                <button
                  onClick={() => setActiveType(isActive ? null : type.id)}
                  className="w-full text-left"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div
                      className="w-8 h-8 flex items-center justify-center text-xs font-medium"
                      style={{
                        backgroundColor: isActive ? "#00FBA9" : "#FAFAFA",
                        color: isActive ? "#171717" : "#A9A9A9",
                      }}
                    >
                      {type.icon}
                    </div>
                    <span
                      className="text-lg font-light"
                      style={{ color: "#171717" }}
                    >
                      {count}
                    </span>
                  </div>
                  <p
                    className="text-sm font-medium"
                    style={{ color: "#171717" }}
                  >
                    {type.label}
                  </p>
                  <p className="text-xs" style={{ color: "#A9A9A9" }}>
                    {type.description}
                  </p>
                </button>
                {isSyncable && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      syncProducts();
                    }}
                    disabled={syncing}
                    className="mt-3 w-full px-3 py-1.5 rounded text-xs font-medium transition-colors"
                    style={{
                      backgroundColor: syncing
                        ? "#E5E5E5"
                        : "rgba(0,251,169,0.15)",
                      color: syncing ? "#A9A9A9" : "#00A672",
                    }}
                  >
                    {syncing ? "Syncing..." : "Sync Products"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        {syncMessage && (
          <div
            className="mt-3 px-4 py-2 rounded text-sm"
            style={{
              backgroundColor: syncMessage.includes("failed")
                ? "rgba(255,76,0,0.1)"
                : "rgba(0,251,169,0.1)",
              color: syncMessage.includes("failed") ? "#FF4C00" : "#00A672",
            }}
          >
            {syncMessage}
          </div>
        )}
      </div>

      {/* Search & Filter Bar */}
      <div className="flex gap-4 mb-6 items-center">
        <div className="flex-1 relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search knowledge..."
            className="w-full pl-10 pr-4 py-2 border text-sm"
            style={{ borderColor: "rgba(169,169,169,0.3)" }}
          />
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#A9A9A9"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21L16.65 16.65" />
          </svg>
        </div>
        {activeType && (
          <button
            onClick={() => setActiveType(null)}
            className="px-3 py-2 rounded-full text-sm flex items-center gap-2"
            style={{ backgroundColor: "#FAFAFA", color: "#A9A9A9" }}
          >
            {KNOWLEDGE_TYPES.find((t) => t.id === activeType)?.label}
            <span style={{ color: "#FF4C00" }}>×</span>
          </button>
        )}
        {selectedItems.size > 0 && (
          <button
            onClick={handleDeleteSelected}
            disabled={deleting}
            className="px-4 py-2 rounded-full text-sm font-medium"
            style={{
              backgroundColor: deleting ? "#E5E5E5" : "#FF4C00",
              color: deleting ? "#A9A9A9" : "#FFFFFF",
            }}
          >
            {deleting ? "Deleting..." : `Delete ${selectedItems.size} item(s)`}
          </button>
        )}
      </div>

      {/* Knowledge Items List */}
      <div
        className="bg-white border overflow-hidden"
        style={{ borderColor: "rgba(169,169,169,0.2)" }}
      >
        {loading ? (
          <div className="p-12 text-center">
            <div
              className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full mx-auto mb-4"
              style={{ borderColor: "#00FBA9" }}
            />
            <p style={{ color: "#A9A9A9" }}>Loading knowledge...</p>
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: "#FAFAFA" }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#A9A9A9"
                strokeWidth="1.5"
              >
                <path
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <p className="mb-2" style={{ color: "#171717" }}>
              No knowledge items yet
            </p>
            <p className="text-sm mb-4" style={{ color: "#A9A9A9" }}>
              Add materials, policies, FAQs, and training docs for your AI
              Educator
            </p>
            <Link
              to="/knowledge/new"
              className="px-4 py-2 rounded-full text-sm font-medium inline-block"
              style={{ backgroundColor: "#00FBA9", color: "#171717" }}
            >
              Add Your First Knowledge
            </Link>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr
                className="border-b"
                style={{ borderColor: "rgba(169,169,169,0.1)" }}
              >
                <th
                  className="text-left text-xs font-medium px-6 py-3"
                  style={{ color: "#A9A9A9", width: "40px" }}
                >
                  <input
                    type="checkbox"
                    checked={
                      selectedItems.size === items.length && items.length > 0
                    }
                    onChange={handleSelectAll}
                    className="cursor-pointer"
                  />
                </th>
                <th
                  className="text-left text-xs font-medium px-6 py-3"
                  style={{ color: "#A9A9A9" }}
                >
                  TITLE
                </th>
                <th
                  className="text-left text-xs font-medium px-4 py-3"
                  style={{ color: "#A9A9A9" }}
                >
                  TYPE
                </th>
                <th
                  className="text-left text-xs font-medium px-4 py-3"
                  style={{ color: "#A9A9A9" }}
                >
                  STATUS
                </th>
                <th
                  className="text-right text-xs font-medium px-6 py-3"
                  style={{ color: "#A9A9A9" }}
                >
                  UPDATED
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.id}
                  className="border-b hover:bg-gray-50 transition-colors"
                  style={{ borderColor: "rgba(169,169,169,0.05)" }}
                >
                  <td
                    className="px-6 py-4"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selectedItems.has(item.id)}
                      onChange={() => handleSelectItem(item.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="cursor-pointer"
                    />
                  </td>
                  <td
                    className="px-6 py-4 cursor-pointer"
                    onClick={() =>
                      (window.location.href = `/knowledge/${item.id}`)
                    }
                  >
                    <div className="flex items-center gap-3">
                      {item.type === "product" && item.metadata?.image_url ? (
                        <>
                          <img
                            src={item.metadata.image_url}
                            alt={item.title}
                            className="w-10 h-10 rounded object-cover flex-shrink-0"
                            onError={(e) => {
                              // Fallback to icon if image fails to load
                              e.currentTarget.style.display = "none";
                              const fallback =
                                e.currentTarget.parentElement?.querySelector(
                                  ".fallback-icon",
                                );
                              if (fallback)
                                (fallback as HTMLElement).style.display =
                                  "flex";
                            }}
                          />
                          <div
                            className="fallback-icon rounded flex items-center justify-center text-xs font-medium flex-shrink-0 hidden"
                            style={{
                              width: "40px",
                              height: "40px",
                              backgroundColor: "#FAFAFA",
                              color: "#A9A9A9",
                            }}
                          >
                            {getTypeIcon(item.type)}
                          </div>
                        </>
                      ) : (
                        <div
                          className="rounded flex items-center justify-center text-xs font-medium flex-shrink-0"
                          style={{
                            width: "40px",
                            height: "40px",
                            backgroundColor: "#FAFAFA",
                            color: "#A9A9A9",
                          }}
                        >
                          {getTypeIcon(item.type)}
                        </div>
                      )}
                      <div>
                        <p
                          className="font-medium text-sm"
                          style={{ color: "#171717" }}
                        >
                          {item.title}
                        </p>
                        {item.summary && (
                          <p
                            className="text-xs truncate max-w-md"
                            style={{ color: "#A9A9A9" }}
                          >
                            {item.summary}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td
                    className="px-4 py-4 cursor-pointer"
                    onClick={() =>
                      (window.location.href = `/knowledge/${item.id}`)
                    }
                  >
                    <span
                      className="text-xs capitalize"
                      style={{ color: "#A9A9A9" }}
                    >
                      {item.type}
                    </span>
                    {item.category && (
                      <span
                        className="text-xs ml-1"
                        style={{ color: "#A9A9A9" }}
                      >
                        / {item.category}
                      </span>
                    )}
                  </td>
                  <td
                    className="px-4 py-4 cursor-pointer"
                    onClick={() =>
                      (window.location.href = `/knowledge/${item.id}`)
                    }
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{
                          backgroundColor: getStatusColor(
                            item.embedding_status,
                          ),
                        }}
                      />
                      <span
                        className="text-xs capitalize"
                        style={{ color: "#A9A9A9" }}
                      >
                        {item.embedding_status}
                      </span>
                      {item.chunk_count > 0 && (
                        <span className="text-xs" style={{ color: "#A9A9A9" }}>
                          ({item.chunk_count} chunks)
                        </span>
                      )}
                    </div>
                  </td>
                  <td
                    className="px-6 py-4 text-right cursor-pointer"
                    onClick={() =>
                      (window.location.href = `/knowledge/${item.id}`)
                    }
                  >
                    <span className="text-xs" style={{ color: "#A9A9A9" }}>
                      {new Date(item.updated_at).toLocaleDateString()}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Quick Actions */}
      <div className="mt-8 grid grid-cols-3 gap-4">
        <QuickActionCard
          title="Test AI Educator"
          description="Ask questions and see what knowledge is used"
          href="/knowledge/test"
          icon={
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
        />
        <QuickActionCardButton
          title="Upload Documents"
          description="Add PDFs, images, or CSV files"
          onClick={() => setShowUploadModal(true)}
          icon={
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
        />
        <QuickActionCard
          title="Sync from Shopify"
          description="Import product catalog automatically"
          href="/knowledge/sync"
          icon={
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
        />
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 w-full max-w-md shadow-xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-medium" style={{ color: "#171717" }}>
                Upload File
              </h2>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadFile(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    d="M6 18L18 6M6 6l12 12"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            {/* File Drop Zone */}
            <div
              className="border-2 border-dashed p-8 text-center mb-4 transition-colors cursor-pointer"
              style={{
                borderColor: uploadFile ? "#00FBA9" : "rgba(169,169,169,0.3)",
                backgroundColor: uploadFile
                  ? "rgba(0,251,169,0.05)"
                  : "transparent",
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const file = e.dataTransfer.files?.[0];
                if (file) setUploadFile(file);
              }}
              onClick={() => document.getElementById("file-input")?.click()}
            >
              <input
                id="file-input"
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) setUploadFile(file);
                }}
              />
              {uploadFile ? (
                <div>
                  <div
                    className="w-12 h-12 mx-auto mb-3 flex items-center justify-center"
                    style={{ backgroundColor: "rgba(0,251,169,0.2)" }}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#00FBA9"
                      strokeWidth="2"
                    >
                      <path
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <p
                    className="text-sm font-medium"
                    style={{ color: "#171717" }}
                  >
                    {uploadFile.name}
                  </p>
                  <p className="text-xs" style={{ color: "#A9A9A9" }}>
                    {(uploadFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              ) : (
                <div>
                  <div
                    className="w-12 h-12 mx-auto mb-3 flex items-center justify-center"
                    style={{ backgroundColor: "#FAFAFA" }}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#A9A9A9"
                      strokeWidth="1.5"
                    >
                      <path
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <p className="text-sm" style={{ color: "#A9A9A9" }}>
                    Drop a file here or{" "}
                    <span style={{ color: "#3941EB" }}>browse</span>
                  </p>
                  <p className="text-xs mt-1" style={{ color: "#A9A9A9" }}>
                    PDF or images (max 10MB)
                  </p>
                </div>
              )}
            </div>

            {/* Title Input */}
            <div className="mb-4">
              <label
                className="block text-xs font-medium mb-1"
                style={{ color: "#A9A9A9" }}
              >
                Title (optional)
              </label>
              <input
                type="text"
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
                placeholder={
                  uploadFile?.name.replace(/\.[^/.]+$/, "") ||
                  "Enter a title..."
                }
                className="w-full px-3 py-2 border text-sm"
                style={{ borderColor: "rgba(169,169,169,0.3)" }}
              />
            </div>

            {/* Type Select */}
            <div className="mb-4">
              <label
                className="block text-xs font-medium mb-1"
                style={{ color: "#A9A9A9" }}
              >
                Knowledge Type
              </label>
              <select
                value={uploadType}
                onChange={(e) => setUploadType(e.target.value)}
                className="w-full px-3 py-2 border text-sm"
                style={{ borderColor: "rgba(169,169,169,0.3)" }}
              >
                <option value="training">Training Document</option>
                <option value="policy">Policy</option>
                <option value="material">Material Info</option>
                <option value="faq">FAQ</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            {/* Category Input */}
            <div className="mb-6">
              <label
                className="block text-xs font-medium mb-1"
                style={{ color: "#A9A9A9" }}
              >
                Category (optional)
              </label>
              <input
                type="text"
                value={uploadCategory}
                onChange={(e) => setUploadCategory(e.target.value)}
                placeholder="e.g., Product Guides, Staff Training"
                className="w-full px-3 py-2 border text-sm"
                style={{ borderColor: "rgba(169,169,169,0.3)" }}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadFile(null);
                }}
                className="flex-1 px-4 py-2 rounded-full text-sm font-medium border"
                style={{
                  borderColor: "rgba(169,169,169,0.3)",
                  color: "#A9A9A9",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleFileUpload}
                disabled={!uploadFile || uploading}
                className="flex-1 px-4 py-2 rounded-full text-sm font-medium"
                style={{
                  backgroundColor:
                    !uploadFile || uploading ? "#E5E5E5" : "#00FBA9",
                  color: !uploadFile || uploading ? "#A9A9A9" : "#171717",
                }}
              >
                {uploading ? "Uploading..." : "Upload & Process"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  subtext,
  color,
}: {
  label: string;
  value: number;
  subtext: string;
  color?: string;
}) {
  return (
    <div
      className="bg-white border p-4"
      style={{ borderColor: "rgba(169,169,169,0.2)" }}
    >
      <p
        className="text-xs uppercase tracking-wider mb-1"
        style={{ color: "#A9A9A9" }}
      >
        {label}
      </p>
      <p className="text-2xl font-light" style={{ color: color || "#171717" }}>
        {value}
      </p>
      <p className="text-xs" style={{ color: "#A9A9A9" }}>
        {subtext}
      </p>
    </div>
  );
}

function QuickActionCard({
  title,
  description,
  href,
  icon,
}: {
  title: string;
  description: string;
  href: string;
  icon: React.ReactNode;
}) {
  return (
    <Link
      to={href}
      className="bg-white border p-4 hover:shadow-md transition-shadow block"
      style={{ borderColor: "rgba(169,169,169,0.2)" }}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: "rgba(0,251,169,0.1)", color: "#00FBA9" }}
        >
          {icon}
        </div>
        <div>
          <p className="font-medium text-sm" style={{ color: "#171717" }}>
            {title}
          </p>
          <p className="text-xs" style={{ color: "#A9A9A9" }}>
            {description}
          </p>
        </div>
      </div>
    </Link>
  );
}

function QuickActionCardButton({
  title,
  description,
  onClick,
  icon,
}: {
  title: string;
  description: string;
  onClick: () => void;
  icon: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className="bg-white border p-4 hover:shadow-md transition-shadow block w-full text-left"
      style={{ borderColor: "rgba(169,169,169,0.2)" }}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: "rgba(0,251,169,0.1)", color: "#00FBA9" }}
        >
          {icon}
        </div>
        <div>
          <p className="font-medium text-sm" style={{ color: "#171717" }}>
            {title}
          </p>
          <p className="text-xs" style={{ color: "#A9A9A9" }}>
            {description}
          </p>
        </div>
      </div>
    </button>
  );
}

// Demo data for Arc'teryx tenant
function getArcteryxDemoStats(): KnowledgeStats {
  return {
    total: 24,
    indexed: 22,
    pending: 2,
    errors: 0,
    chunks: 156,
    countsByType: {
      product: 10,
      material: 5,
      policy: 4,
      faq: 3,
      training: 2,
      custom: 0,
    },
    countsByStatus: {
      indexed: 22,
      pending: 2,
      processing: 0,
      error: 0,
    },
    categories: [
      "Shell Technologies",
      "Insulation",
      "Trade-In Program",
      "Product Care",
      "Warranty",
    ],
    recentItems: [
      {
        id: "k-1",
        type: "material",
        title: "GORE-TEX Pro Technology",
        embedding_status: "indexed",
        updated_at: "2024-12-18T10:00:00Z",
      },
      {
        id: "k-2",
        type: "policy",
        title: "Trade-In Program Terms",
        embedding_status: "indexed",
        updated_at: "2024-12-17T10:00:00Z",
      },
      {
        id: "k-3",
        type: "faq",
        title: "Warranty Coverage FAQ",
        embedding_status: "indexed",
        updated_at: "2024-12-16T10:00:00Z",
      },
    ],
    healthScore: 92,
  };
}

function getArcteryxDemoKnowledge(): KnowledgeItem[] {
  return [
    // Materials
    {
      id: "k-mat-1",
      type: "material",
      category: "Shell Technologies",
      title: "GORE-TEX Pro Technology",
      summary:
        "The most breathable, most rugged, and durably waterproof GORE-TEX product technology. Used in our most demanding alpine shells.",
      source_type: "manual",
      embedding_status: "indexed",
      chunk_count: 8,
      is_published: 1,
      updated_at: "2024-12-15T10:00:00Z",
      tags: ["gore-tex", "waterproof", "breathable", "alpine"],
    },
    {
      id: "k-mat-2",
      type: "material",
      category: "Shell Technologies",
      title: "GORE-TEX with C-KNIT Backer",
      summary:
        "Lighter, more comfortable shell technology with a circular knit backer for next-to-skin comfort. Ideal for hiking and trekking.",
      source_type: "manual",
      embedding_status: "indexed",
      chunk_count: 6,
      is_published: 1,
      updated_at: "2024-12-14T10:00:00Z",
      tags: ["gore-tex", "c-knit", "comfort", "hiking"],
    },
    {
      id: "k-mat-3",
      type: "material",
      category: "Insulation",
      title: "Coreloft™ Synthetic Insulation",
      summary:
        "Our proprietary synthetic insulation that provides excellent warmth-to-weight ratio and retains insulating properties when wet.",
      source_type: "manual",
      embedding_status: "indexed",
      chunk_count: 5,
      is_published: 1,
      updated_at: "2024-12-13T10:00:00Z",
      tags: ["coreloft", "insulation", "synthetic", "warmth"],
    },
    {
      id: "k-mat-4",
      type: "material",
      category: "Insulation",
      title: "850-Fill European Goose Down",
      summary:
        "Premium goose down with exceptional loft and warmth. Ethically sourced and RDS certified. Used in our Cerium series.",
      source_type: "manual",
      embedding_status: "indexed",
      chunk_count: 4,
      is_published: 1,
      updated_at: "2024-12-12T10:00:00Z",
      tags: ["down", "goose-down", "insulation", "warmth"],
    },
    {
      id: "k-mat-5",
      type: "material",
      category: "Fabrics",
      title: "Fortius™ Softshell Fabric",
      summary:
        "Stretchy, breathable softshell material for high-output activities. Provides weather resistance and freedom of movement.",
      source_type: "manual",
      embedding_status: "indexed",
      chunk_count: 4,
      is_published: 1,
      updated_at: "2024-12-11T10:00:00Z",
      tags: ["softshell", "stretch", "breathable", "climbing"],
    },
    // Policies
    {
      id: "k-pol-1",
      type: "policy",
      category: "Trade-In Program",
      title: "Trade-In Program Terms & Conditions",
      summary:
        "Complete terms for Arc'teryx ReBird trade-in program. Includes eligibility requirements, credit values, and product conditions.",
      source_type: "manual",
      embedding_status: "indexed",
      chunk_count: 12,
      is_published: 1,
      updated_at: "2024-12-18T10:00:00Z",
      tags: ["trade-in", "rebird", "terms", "conditions"],
    },
    {
      id: "k-pol-2",
      type: "policy",
      category: "Warranty",
      title: "Limited Lifetime Warranty",
      summary:
        "Arc'teryx products are warranted against defects in materials and workmanship for the practical lifetime of the product.",
      source_type: "manual",
      embedding_status: "indexed",
      chunk_count: 8,
      is_published: 1,
      updated_at: "2024-12-17T10:00:00Z",
      tags: ["warranty", "lifetime", "defects", "repairs"],
    },
    {
      id: "k-pol-3",
      type: "policy",
      category: "Trade-In Program",
      title: "Product Condition Grading Guide",
      summary:
        "How we assess trade-in items: Like New (90%+ credit), Excellent (70-89%), Good (50-69%), Fair (30-49%).",
      source_type: "manual",
      embedding_status: "indexed",
      chunk_count: 6,
      is_published: 1,
      updated_at: "2024-12-16T10:00:00Z",
      tags: ["grading", "condition", "trade-in", "assessment"],
    },
    {
      id: "k-pol-4",
      type: "policy",
      category: "Returns",
      title: "Return & Exchange Policy",
      summary:
        "30-day return policy for unworn items with original tags. Extended holiday return window. Store credit for worn items.",
      source_type: "manual",
      embedding_status: "indexed",
      chunk_count: 5,
      is_published: 1,
      updated_at: "2024-12-15T10:00:00Z",
      tags: ["returns", "exchanges", "refunds", "30-day"],
    },
    // FAQs
    {
      id: "k-faq-1",
      type: "faq",
      category: "Trade-In Program",
      title: "How does trade-in credit work?",
      summary:
        "Trade-in credits are issued as store credit valid for 12 months. Credits can be used online or in-store for any Arc'teryx products.",
      source_type: "manual",
      embedding_status: "indexed",
      chunk_count: 3,
      is_published: 1,
      updated_at: "2024-12-18T10:00:00Z",
      tags: ["trade-in", "credit", "store-credit", "faq"],
    },
    {
      id: "k-faq-2",
      type: "faq",
      category: "Product Care",
      title: "How to wash GORE-TEX jackets?",
      summary:
        "Machine wash warm with tech wash, no fabric softener. Tumble dry on medium heat to reactivate DWR. Avoid dry cleaning.",
      source_type: "manual",
      embedding_status: "indexed",
      chunk_count: 4,
      is_published: 1,
      updated_at: "2024-12-17T10:00:00Z",
      tags: ["care", "washing", "gore-tex", "maintenance"],
    },
    {
      id: "k-faq-3",
      type: "faq",
      category: "Warranty",
      title: "What does the warranty cover?",
      summary:
        "Warranty covers manufacturing defects. Does not cover normal wear and tear, damage from accidents, or improper care.",
      source_type: "manual",
      embedding_status: "indexed",
      chunk_count: 3,
      is_published: 1,
      updated_at: "2024-12-16T10:00:00Z",
      tags: ["warranty", "coverage", "defects", "faq"],
    },
    // Training
    {
      id: "k-train-1",
      type: "training",
      category: "Product Knowledge",
      title: "Alpha vs Beta Shell Line Guide",
      summary:
        "Training guide on the differences between Alpha (climbing-focused) and Beta (all-around mountain) shell lines.",
      source_type: "upload",
      embedding_status: "indexed",
      chunk_count: 15,
      is_published: 1,
      updated_at: "2024-12-10T10:00:00Z",
      tags: ["training", "alpha", "beta", "shells", "product-knowledge"],
    },
    {
      id: "k-train-2",
      type: "training",
      category: "Trade-In Assessment",
      title: "Trade-In Condition Assessment Training",
      summary:
        "Staff training document for assessing trade-in item conditions. Includes photo examples and grading criteria.",
      source_type: "upload",
      embedding_status: "pending",
      chunk_count: 0,
      is_published: 1,
      updated_at: "2024-12-18T14:00:00Z",
      tags: ["training", "assessment", "grading", "trade-in"],
    },
    // Products (sample)
    {
      id: "k-prod-1",
      type: "product",
      category: "Shell Jackets",
      title: "Beta AR Jacket",
      summary:
        "Versatile GORE-TEX Pro shell for all-around mountain use. Features helmet-compatible hood and pit zips.",
      source_type: "sync",
      embedding_status: "indexed",
      chunk_count: 6,
      is_published: 1,
      updated_at: "2024-12-18T10:00:00Z",
      tags: ["beta", "shell", "gore-tex", "jacket"],
      metadata: { image_url: "/images/products/arcteryx/beta-ar-jacket.jpeg" },
    },
    {
      id: "k-prod-2",
      type: "product",
      category: "Shell Jackets",
      title: "Alpha SV Jacket",
      summary:
        "The pinnacle of alpine protection. Most durable GORE-TEX Pro construction for severe conditions.",
      source_type: "sync",
      embedding_status: "indexed",
      chunk_count: 6,
      is_published: 1,
      updated_at: "2024-12-18T10:00:00Z",
      tags: ["alpha", "shell", "gore-tex", "alpine"],
      metadata: { image_url: "/images/products/arcteryx/alpha-sv-jacket.jpeg" },
    },
    {
      id: "k-prod-3",
      type: "product",
      category: "Insulated Jackets",
      title: "Atom LT Hoody",
      summary:
        "Lightweight, breathable synthetic insulated hoody. Perfect for active use or as a midlayer.",
      source_type: "sync",
      embedding_status: "indexed",
      chunk_count: 5,
      is_published: 1,
      updated_at: "2024-12-18T10:00:00Z",
      tags: ["atom", "insulated", "coreloft", "midlayer"],
      metadata: { image_url: "/images/products/arcteryx/atom-lt-hoody.jpeg" },
    },
    {
      id: "k-prod-4",
      type: "product",
      category: "Insulated Jackets",
      title: "Cerium LT Jacket",
      summary:
        "Superlight down jacket with exceptional warmth-to-weight. 850-fill goose down for efficient warmth.",
      source_type: "sync",
      embedding_status: "indexed",
      chunk_count: 5,
      is_published: 1,
      updated_at: "2024-12-18T10:00:00Z",
      tags: ["cerium", "down", "lightweight", "warmth"],
      metadata: {
        image_url: "/images/products/arcteryx/cerium-lt-jacket.jpeg",
      },
    },
    {
      id: "k-prod-5",
      type: "product",
      category: "Shell Jackets",
      title: "Zeta SL Jacket",
      summary:
        "Ultralight emergency shell for hiking. Packable GORE-TEX protection when you need it.",
      source_type: "sync",
      embedding_status: "indexed",
      chunk_count: 4,
      is_published: 1,
      updated_at: "2024-12-18T10:00:00Z",
      tags: ["zeta", "shell", "gore-tex", "ultralight"],
      metadata: { image_url: "/images/products/arcteryx/zeta-sl-jacket.jpeg" },
    },
    {
      id: "k-prod-6",
      type: "product",
      category: "Fleece",
      title: "Covert Cardigan",
      summary:
        "Polartec® fleece cardigan with clean styling. Versatile piece for everyday wear.",
      source_type: "sync",
      embedding_status: "indexed",
      chunk_count: 4,
      is_published: 1,
      updated_at: "2024-12-18T10:00:00Z",
      tags: ["covert", "fleece", "polartec", "casual"],
      metadata: { image_url: "/images/products/arcteryx/covert-cardigan.jpeg" },
    },
    {
      id: "k-prod-7",
      type: "product",
      category: "Fleece",
      title: "Delta LT Zip Neck",
      summary:
        "Lightweight fleece for active use. Polartec® grid fleece for breathability and warmth.",
      source_type: "sync",
      embedding_status: "indexed",
      chunk_count: 4,
      is_published: 1,
      updated_at: "2024-12-18T10:00:00Z",
      tags: ["delta", "fleece", "polartec", "active"],
      metadata: {
        image_url: "/images/products/arcteryx/delta-lt-zip-neck.jpeg",
      },
    },
    {
      id: "k-prod-8",
      type: "product",
      category: "Pants",
      title: "Gamma LT Pants",
      summary:
        "Lightweight softshell pants for hiking and climbing. Stretch fabric for freedom of movement.",
      source_type: "sync",
      embedding_status: "indexed",
      chunk_count: 4,
      is_published: 1,
      updated_at: "2024-12-18T10:00:00Z",
      tags: ["gamma", "pants", "softshell", "hiking"],
      metadata: { image_url: "/images/products/arcteryx/gamma-lt-pants.jpeg" },
    },
    {
      id: "k-prod-9",
      type: "product",
      category: "Insulated Jackets",
      title: "Proton FL Hoody",
      summary:
        "Air-permeable insulated hoody for high-output activities. Octa™ Loft insulation for breathability.",
      source_type: "sync",
      embedding_status: "indexed",
      chunk_count: 5,
      is_published: 1,
      updated_at: "2024-12-18T10:00:00Z",
      tags: ["proton", "insulated", "active", "breathable"],
      metadata: { image_url: "/images/products/arcteryx/proton-fl-hoody.jpeg" },
    },
    {
      id: "k-prod-10",
      type: "product",
      category: "Shell Jackets",
      title: "Beta LT Jacket",
      summary:
        "Lightweight GORE-TEX shell for trekking. Reduced weight without sacrificing weather protection.",
      source_type: "sync",
      embedding_status: "pending",
      chunk_count: 0,
      is_published: 1,
      updated_at: "2024-12-18T14:00:00Z",
      tags: ["beta", "shell", "gore-tex", "lightweight"],
      metadata: { image_url: "/images/products/arcteryx/beta-lt-jacket.jpeg" },
    },
  ];
}

function getLululemonDemoStats(): KnowledgeStats {
  return {
    total: 35,
    indexed: 33,
    pending: 2,
    errors: 0,
    chunks: 245,
    countsByType: {
      product: 8,
      material: 6,
      policy: 5,
      faq: 13,
      training: 3,
      custom: 0,
    },
    countsByStatus: {
      indexed: 33,
      pending: 2,
      processing: 0,
      error: 0,
    },
    categories: [
      "Performance Fabrics",
      "Care Instructions",
      "Trade-In Program",
      "Sizing",
      "Products",
      "Educator Training",
    ],
    recentItems: [
      {
        id: "lk-1",
        type: "material",
        title: "Nulu™ Fabric",
        embedding_status: "indexed",
        updated_at: "2024-12-18T10:00:00Z",
      },
      {
        id: "lk-2",
        type: "policy",
        title: "Like New Trade-In Guidelines",
        embedding_status: "indexed",
        updated_at: "2024-12-17T10:00:00Z",
      },
      {
        id: "lk-3",
        type: "faq",
        title: "How do Align pants fit?",
        embedding_status: "indexed",
        updated_at: "2024-12-16T10:00:00Z",
      },
    ],
    healthScore: 94,
  };
}

function getLululemonDemoKnowledge(): KnowledgeItem[] {
  return [
    // Materials
    {
      id: "lk-mat-1",
      type: "material",
      category: "Performance Fabrics",
      title: "Nulu™ Fabric",
      summary:
        "Buttery-soft, lightweight fabric with a naked sensation. Used in Align collection. Provides four-way stretch and sweat-wicking properties.",
      source_type: "upload",
      embedding_status: "indexed",
      chunk_count: 3,
      is_published: 1,
      updated_at: "2024-12-15T10:00:00Z",
      tags: ["nulu", "yoga", "stretch", "lightweight"],
    },
    {
      id: "lk-mat-2",
      type: "material",
      category: "Performance Fabrics",
      title: "Everlux™ Fabric",
      summary:
        "Quick-drying, cool-to-touch fabric for intense training. Used in Wunder Train collection. Provides medium compression and support.",
      source_type: "upload",
      embedding_status: "indexed",
      chunk_count: 3,
      is_published: 1,
      updated_at: "2024-12-15T11:00:00Z",
      tags: ["everlux", "training", "quick-dry", "cool"],
    },
    {
      id: "lk-mat-3",
      type: "material",
      category: "Performance Fabrics",
      title: "Luon® Fabric",
      summary:
        "Signature cotton-blend fabric for studio practice. Four-way stretch with sweat-wicking properties. Great for both low and high intensity.",
      source_type: "upload",
      embedding_status: "indexed",
      chunk_count: 4,
      is_published: 1,
      updated_at: "2024-12-15T12:00:00Z",
      tags: ["luon", "cotton", "studio", "versatile"],
    },
    {
      id: "lk-mat-4",
      type: "material",
      category: "Performance Fabrics",
      title: "Luxtreme® Fabric",
      summary:
        "Smooth, sleek fabric with advanced sweat-wicking. Offers medium compression and four-way stretch. Ideal for running and cycling.",
      source_type: "upload",
      embedding_status: "indexed",
      chunk_count: 4,
      is_published: 1,
      updated_at: "2024-12-14T10:00:00Z",
      tags: ["luxtreme", "running", "cycling", "compression"],
    },
    {
      id: "lk-mat-5",
      type: "material",
      category: "Performance Fabrics",
      title: "Rulu™ Fabric",
      summary:
        "Soft, lightweight fabric with a brushed interior. Provides warmth without bulk. Perfect for cooler weather running and outdoor activities.",
      source_type: "upload",
      embedding_status: "indexed",
      chunk_count: 3,
      is_published: 1,
      updated_at: "2024-12-13T10:00:00Z",
      tags: ["rulu", "warmth", "running", "outdoor"],
    },
    {
      id: "lk-mat-6",
      type: "material",
      category: "Performance Fabrics",
      title: "Swift Fabric",
      summary:
        "Lightweight, sweat-wicking fabric designed for high-speed activities. Features four-way stretch and excellent breathability.",
      source_type: "upload",
      embedding_status: "indexed",
      chunk_count: 3,
      is_published: 1,
      updated_at: "2024-12-12T10:00:00Z",
      tags: ["swift", "running", "breathable", "lightweight"],
    },
    // Policies
    {
      id: "lk-pol-1",
      type: "policy",
      category: "Trade-In Program",
      title: "Like New Trade-In Guidelines",
      summary:
        "Accept gently used lululemon gear for store credit. Items must be clean, damage-free, and within 5 years of purchase.",
      source_type: "upload",
      embedding_status: "indexed",
      chunk_count: 4,
      is_published: 1,
      updated_at: "2024-12-14T10:00:00Z",
      tags: ["trade-in", "resale", "sustainability", "credit"],
    },
    {
      id: "lk-pol-2",
      type: "policy",
      category: "Quality Promise",
      title: "Quality Promise Program",
      summary:
        "We stand behind our products. If an item doesn't perform as intended, bring it back for a replacement or refund.",
      source_type: "upload",
      embedding_status: "indexed",
      chunk_count: 3,
      is_published: 1,
      updated_at: "2024-12-14T11:00:00Z",
      tags: ["quality", "guarantee", "returns", "promise"],
    },
    {
      id: "lk-pol-3",
      type: "policy",
      category: "Trade-In Program",
      title: "Condition Grading Standards",
      summary:
        "Trade-in credit tiers: Excellent (like new) 50%, Good (gently worn) 35%, Fair (visible wear) 20%. Items must be clean and functional.",
      source_type: "upload",
      embedding_status: "indexed",
      chunk_count: 3,
      is_published: 1,
      updated_at: "2024-12-13T10:00:00Z",
      tags: ["grading", "condition", "trade-in", "credit"],
    },
    {
      id: "lk-pol-4",
      type: "policy",
      category: "Returns",
      title: "Return & Exchange Policy",
      summary:
        "30-day return policy for unworn items with tags attached. Exchanges available for different sizes or colors. Final sale items not eligible.",
      source_type: "upload",
      embedding_status: "indexed",
      chunk_count: 4,
      is_published: 1,
      updated_at: "2024-12-12T10:00:00Z",
      tags: ["returns", "exchange", "30-day", "refund"],
    },
    {
      id: "lk-pol-5",
      type: "policy",
      category: "Hemming",
      title: "Complimentary Hemming Service",
      summary:
        "Free hemming on any lululemon bottoms, new or old. Bring items to any store. Turnaround typically 3-5 days.",
      source_type: "upload",
      embedding_status: "indexed",
      chunk_count: 2,
      is_published: 1,
      updated_at: "2024-12-11T10:00:00Z",
      tags: ["hemming", "alterations", "free", "service"],
    },
    // FAQs
    {
      id: "lk-faq-1",
      type: "faq",
      category: "Sizing",
      title: "How do Align pants fit?",
      summary:
        "Align pants run true to size with a high-rise fit. The Nulu fabric provides a naked sensation with light compression.",
      source_type: "manual",
      embedding_status: "indexed",
      chunk_count: 2,
      is_published: 1,
      updated_at: "2024-12-13T10:00:00Z",
      tags: ["align", "sizing", "fit", "leggings"],
    },
    {
      id: "lk-faq-2",
      type: "faq",
      category: "Care Instructions",
      title: "How to wash performance fabrics",
      summary:
        "Machine wash cold with like fabrics. Avoid fabric softener and dryer sheets. Tumble dry low or hang to dry.",
      source_type: "manual",
      embedding_status: "indexed",
      chunk_count: 2,
      is_published: 1,
      updated_at: "2024-12-13T11:00:00Z",
      tags: ["care", "washing", "maintenance", "fabric"],
    },
    {
      id: "lk-faq-3",
      type: "faq",
      category: "Products",
      title: "What's the difference between Wunder Train and Wunder Under?",
      summary:
        "Wunder Train uses Everlux for training, while Wunder Under uses Luon for studio practice. Train is more compressive and sweat-wicking.",
      source_type: "manual",
      embedding_status: "indexed",
      chunk_count: 2,
      is_published: 1,
      updated_at: "2024-12-13T12:00:00Z",
      tags: ["wunder", "comparison", "training", "studio"],
    },
    {
      id: "lk-faq-4",
      type: "faq",
      category: "Sizing",
      title: "What size Scuba hoodie should I get?",
      summary:
        "Scuba Oversized uses alpha sizing (XS/S, M/L, XL/XXL). For fitted Scuba styles, size up if between sizes. Check product page for specific fit notes.",
      source_type: "manual",
      embedding_status: "indexed",
      chunk_count: 2,
      is_published: 1,
      updated_at: "2024-12-12T10:00:00Z",
      tags: ["scuba", "sizing", "hoodie", "oversized"],
    },
    {
      id: "lk-faq-5",
      type: "faq",
      category: "Products",
      title: "Which leggings are best for running?",
      summary:
        "Fast and Free (Nulux), Swift Speed (Luxtreme), and Wunder Train (Everlux) are designed for running. All offer compression, sweat-wicking, and secure pockets.",
      source_type: "manual",
      embedding_status: "indexed",
      chunk_count: 2,
      is_published: 1,
      updated_at: "2024-12-11T10:00:00Z",
      tags: ["running", "leggings", "recommendation", "workout"],
    },
    {
      id: "lk-faq-6",
      type: "faq",
      category: "Products",
      title: "What's the best sports bra for high impact?",
      summary:
        "Energy Bra and Free to Be Elevated offer high support for running, HIIT, and jumping. Look for encapsulated cups and adjustable straps for maximum support.",
      source_type: "manual",
      embedding_status: "indexed",
      chunk_count: 2,
      is_published: 1,
      updated_at: "2024-12-10T10:00:00Z",
      tags: ["sports-bra", "high-impact", "support", "running"],
    },
    {
      id: "lk-faq-7",
      type: "faq",
      category: "Trade-In Program",
      title: "What items qualify for trade-in?",
      summary:
        "Most lululemon apparel qualifies including leggings, tops, bras, jackets, and shorts. Underwear, swimwear, and heavily damaged items not accepted.",
      source_type: "manual",
      embedding_status: "indexed",
      chunk_count: 2,
      is_published: 1,
      updated_at: "2024-12-09T10:00:00Z",
      tags: ["trade-in", "eligible", "items", "program"],
    },
    {
      id: "lk-faq-8",
      type: "faq",
      category: "Care Instructions",
      title: "Why is fabric softener bad for technical fabrics?",
      summary:
        "Fabric softener coats fibers and blocks sweat-wicking properties. It can also break down stretch fibers over time. Use sport-specific detergent instead.",
      source_type: "manual",
      embedding_status: "indexed",
      chunk_count: 2,
      is_published: 1,
      updated_at: "2024-12-08T10:00:00Z",
      tags: ["care", "fabric-softener", "technical", "maintenance"],
    },
    {
      id: "lk-faq-9",
      type: "faq",
      category: "Products",
      title: "Do you have plus sizes?",
      summary:
        "Yes! Many styles available in sizes 0-20. Extended sizing available online and in select stores. Filter by size on our website to find available styles.",
      source_type: "manual",
      embedding_status: "indexed",
      chunk_count: 2,
      is_published: 1,
      updated_at: "2024-12-07T10:00:00Z",
      tags: ["sizing", "plus-size", "inclusive", "availability"],
    },
    {
      id: "lk-faq-10",
      type: "faq",
      category: "Products",
      title: "What length leggings should I get?",
      summary:
        '25" hits above ankle for most heights. 28" is full length. 23" is 7/8 crop. For tall guests, 31" available in select styles. Hemming is free!',
      source_type: "manual",
      embedding_status: "indexed",
      chunk_count: 2,
      is_published: 1,
      updated_at: "2024-12-06T10:00:00Z",
      tags: ["inseam", "length", "leggings", "sizing"],
    },
    // Products
    {
      id: "lk-prod-1",
      type: "product",
      category: "Leggings",
      title: 'Align High-Rise Pant 25"',
      summary:
        "Our most popular legging. Buttery-soft Nulu fabric for yoga and everyday wear. Lightweight with a naked sensation.",
      source_type: "sync",
      embedding_status: "indexed",
      chunk_count: 5,
      is_published: 1,
      updated_at: "2024-12-18T10:00:00Z",
      tags: ["align", "leggings", "yoga", "nulu"],
    },
    {
      id: "lk-prod-2",
      type: "product",
      category: "Jackets",
      title: "Define Jacket",
      summary:
        "Fitted Luon jacket with thumbholes. Four-way stretch and sweat-wicking. Perfect for warm-ups and cool-downs.",
      source_type: "sync",
      embedding_status: "indexed",
      chunk_count: 4,
      is_published: 1,
      updated_at: "2024-12-18T11:00:00Z",
      tags: ["define", "jacket", "luon", "studio"],
    },
    {
      id: "lk-prod-3",
      type: "product",
      category: "Leggings",
      title: 'Wunder Train High-Rise Tight 25"',
      summary:
        "Everlux fabric that's quick-drying and cool to touch. Designed for training with medium compression and side pockets.",
      source_type: "sync",
      embedding_status: "indexed",
      chunk_count: 4,
      is_published: 1,
      updated_at: "2024-12-18T12:00:00Z",
      tags: ["wunder-train", "leggings", "training", "everlux"],
    },
    {
      id: "lk-prod-4",
      type: "product",
      category: "Tops",
      title: "Scuba Oversized Full-Zip Hoodie",
      summary:
        "Cozy Cotton Fleece with oversized fit. Features a roomy hood and kangaroo pocket. Perfect for post-workout or everyday wear.",
      source_type: "sync",
      embedding_status: "indexed",
      chunk_count: 4,
      is_published: 1,
      updated_at: "2024-12-17T10:00:00Z",
      tags: ["scuba", "hoodie", "oversized", "cozy"],
    },
    {
      id: "lk-prod-5",
      type: "product",
      category: "Bras",
      title: "Energy Bra High Support",
      summary:
        "High-support bra for running and training. Features adjustable straps and a racerback design. Luxtreme fabric for smooth feel.",
      source_type: "sync",
      embedding_status: "indexed",
      chunk_count: 3,
      is_published: 1,
      updated_at: "2024-12-17T11:00:00Z",
      tags: ["energy-bra", "sports-bra", "high-support", "running"],
    },
    {
      id: "lk-prod-6",
      type: "product",
      category: "Leggings",
      title: 'Fast and Free High-Rise Tight 25"',
      summary:
        "Ultralight Nulux fabric with weightless coverage. Designed for running with reflective details and secure waistband pocket.",
      source_type: "sync",
      embedding_status: "indexed",
      chunk_count: 4,
      is_published: 1,
      updated_at: "2024-12-16T10:00:00Z",
      tags: ["fast-free", "running", "nulux", "lightweight"],
    },
    {
      id: "lk-prod-7",
      type: "product",
      category: "Shorts",
      title: 'Hotty Hot High-Rise Short 4"',
      summary:
        "Lightweight Swift fabric for running. Features a continuous drawcord and side pockets. Relaxed fit through hips and thighs.",
      source_type: "sync",
      embedding_status: "indexed",
      chunk_count: 3,
      is_published: 1,
      updated_at: "2024-12-16T11:00:00Z",
      tags: ["hotty-hot", "shorts", "running", "swift"],
    },
    {
      id: "lk-prod-8",
      type: "product",
      category: "Tops",
      title: "Swiftly Tech Long Sleeve 2.0",
      summary:
        "Seamless construction to reduce chafing. Silverescent technology for odor control. Perfect for running and training.",
      source_type: "sync",
      embedding_status: "indexed",
      chunk_count: 3,
      is_published: 1,
      updated_at: "2024-12-15T10:00:00Z",
      tags: ["swiftly", "long-sleeve", "running", "seamless"],
    },
    // Training
    {
      id: "lk-train-1",
      type: "training",
      category: "Educator Training",
      title: "Fabric Technology Deep Dive",
      summary:
        "Comprehensive guide to all lululemon fabrics including Nulu, Everlux, Luon, Luxtreme, and Swift. Covers properties, care, and product applications.",
      source_type: "upload",
      embedding_status: "indexed",
      chunk_count: 12,
      is_published: 1,
      updated_at: "2024-12-10T10:00:00Z",
      tags: ["training", "fabric", "educator", "technical"],
    },
    {
      id: "lk-train-2",
      type: "training",
      category: "Trade-In Assessment",
      title: "Like New Condition Assessment Guide",
      summary:
        "Step-by-step guide for assessing trade-in items. Includes photo examples of excellent, good, and fair conditions. Covers common issues and edge cases.",
      source_type: "upload",
      embedding_status: "indexed",
      chunk_count: 10,
      is_published: 1,
      updated_at: "2024-12-09T10:00:00Z",
      tags: ["training", "assessment", "trade-in", "condition"],
    },
    {
      id: "lk-train-3",
      type: "training",
      category: "Fit Sessions",
      title: "Complimentary Fit Session Best Practices",
      summary:
        "Guide for conducting fit sessions with guests. Covers body type considerations, activity-based recommendations, and creating personalized experiences.",
      source_type: "upload",
      embedding_status: "indexed",
      chunk_count: 8,
      is_published: 1,
      updated_at: "2024-12-08T10:00:00Z",
      tags: ["training", "fit-session", "guest-experience", "sizing"],
    },
  ];
}

function getNespressoDemoStats(): KnowledgeStats {
  return {
    total: 28,
    indexed: 26,
    pending: 2,
    errors: 0,
    chunks: 185,
    countsByType: {
      product: 4,
      material: 5,
      policy: 6,
      faq: 10,
      training: 2,
      custom: 1,
    },
    countsByStatus: {
      indexed: 26,
      pending: 2,
      processing: 0,
      error: 0,
    },
    categories: [
      "Recycling Materials",
      "Rewards Program",
      "Products",
      "Sustainability",
      "Coffee Knowledge",
      "Machines",
    ],
    recentItems: [
      {
        id: "nk-1",
        type: "material",
        title: "Vertuo Centrifusion Technology",
        embedding_status: "indexed",
        updated_at: "2024-12-18T10:00:00Z",
      },
      {
        id: "nk-2",
        type: "policy",
        title: "Recycling Rewards Program",
        embedding_status: "indexed",
        updated_at: "2024-12-17T10:00:00Z",
      },
      {
        id: "nk-3",
        type: "faq",
        title: "How do I make lattes and cappuccinos?",
        embedding_status: "indexed",
        updated_at: "2024-12-16T10:00:00Z",
      },
    ],
    healthScore: 93,
  };
}

function getNespressoDemoKnowledge(): KnowledgeItem[] {
  return [
    // Materials (Recycling)
    {
      id: "nk-mat-1",
      type: "material",
      category: "Recycling Materials",
      title: "Aluminum Capsule Recycling",
      summary:
        "Nespresso capsules are made from aluminum, infinitely recyclable. Collection points turn used capsules into new products.",
      source_type: "upload",
      embedding_status: "indexed",
      chunk_count: 4,
      is_published: 1,
      updated_at: "2024-12-15T10:00:00Z",
      tags: ["aluminum", "recycling", "sustainability", "capsules"],
    },
    {
      id: "nk-mat-2",
      type: "material",
      category: "Recycling Materials",
      title: "Coffee Grounds Composting",
      summary:
        "Used coffee grounds are separated and composted. They become nutrient-rich soil for farms and gardens.",
      source_type: "upload",
      embedding_status: "indexed",
      chunk_count: 3,
      is_published: 1,
      updated_at: "2024-12-15T11:00:00Z",
      tags: ["compost", "coffee", "soil", "organic"],
    },
    // Policies
    {
      id: "nk-pol-1",
      type: "policy",
      category: "Recycling Program",
      title: "Capsule Collection Program",
      summary:
        "Drop off used capsules at any Nespresso boutique or collection point. Free recycling bags available for mail-in return.",
      source_type: "upload",
      embedding_status: "indexed",
      chunk_count: 4,
      is_published: 1,
      updated_at: "2024-12-14T10:00:00Z",
      tags: ["collection", "recycling", "drop-off", "mail-in"],
    },
    {
      id: "nk-pol-2",
      type: "policy",
      category: "Rewards Program",
      title: "Recycling Rewards Points",
      summary:
        "Earn 2 points per capsule recycled. Redeem points for free coffee, accessories, or exclusive experiences.",
      source_type: "upload",
      embedding_status: "indexed",
      chunk_count: 3,
      is_published: 1,
      updated_at: "2024-12-14T11:00:00Z",
      tags: ["rewards", "points", "incentive", "loyalty"],
    },
    // FAQs
    {
      id: "nk-faq-1",
      type: "faq",
      category: "Recycling",
      title: "Where can I recycle Nespresso capsules?",
      summary:
        "Recycle at Nespresso boutiques, collection points, or mail using free recycling bags. Find locations at nespresso.com.",
      source_type: "manual",
      embedding_status: "indexed",
      chunk_count: 2,
      is_published: 1,
      updated_at: "2024-12-13T10:00:00Z",
      tags: ["locations", "recycling", "boutique", "collection"],
    },
    {
      id: "nk-faq-2",
      type: "faq",
      category: "Products",
      title: "What's the difference between Original and Vertuo?",
      summary:
        "Original uses 19-bar pressure for espresso. Vertuo uses Centrifusion for various cup sizes from espresso to carafe.",
      source_type: "manual",
      embedding_status: "indexed",
      chunk_count: 2,
      is_published: 1,
      updated_at: "2024-12-13T11:00:00Z",
      tags: ["original", "vertuo", "comparison", "systems"],
    },
    {
      id: "nk-faq-3",
      type: "faq",
      category: "Sustainability",
      title: "How much of a capsule is recycled?",
      summary:
        "100% of the aluminum and coffee grounds are recycled. Aluminum becomes new products; grounds become compost.",
      source_type: "manual",
      embedding_status: "indexed",
      chunk_count: 2,
      is_published: 1,
      updated_at: "2024-12-13T12:00:00Z",
      tags: ["percentage", "aluminum", "coffee", "sustainability"],
    },
    // Products
    {
      id: "nk-prod-1",
      type: "product",
      category: "Vertuo Coffee",
      title: "Vertuo Intenso",
      summary:
        "Rich and bold coffee with notes of wood and leather. Intensity 9/13. Perfect for a strong morning coffee.",
      source_type: "sync",
      embedding_status: "indexed",
      chunk_count: 3,
      is_published: 1,
      updated_at: "2024-12-18T10:00:00Z",
      tags: ["vertuo", "intenso", "bold", "intensity-9"],
    },
    {
      id: "nk-prod-2",
      type: "product",
      category: "Original Coffee",
      title: "Ristretto",
      summary:
        "Powerful and contrasted blend with fine acidity. Intensity 10/13. Short and intense espresso.",
      source_type: "sync",
      embedding_status: "indexed",
      chunk_count: 3,
      is_published: 1,
      updated_at: "2024-12-18T11:00:00Z",
      tags: ["original", "ristretto", "espresso", "intensity-10"],
    },
  ];
}

function getLegoDemoStats(): KnowledgeStats {
  return {
    total: 31,
    indexed: 29,
    pending: 2,
    errors: 0,
    chunks: 198,
    countsByType: {
      product: 10,
      material: 3,
      policy: 6,
      faq: 8,
      training: 3,
      custom: 1,
    },
    countsByStatus: {
      indexed: 29,
      pending: 2,
      processing: 0,
      error: 0,
    },
    categories: [
      "Brick Materials",
      "Trade-In Program",
      "Building Tips",
      "Products",
      "Authentication",
      "Collecting",
    ],
    recentItems: [
      {
        id: "lego-k-1",
        type: "material",
        title: "ABS Plastic - The LEGO Brick Material",
        embedding_status: "indexed",
        updated_at: "2024-12-18T10:00:00Z",
      },
      {
        id: "lego-k-2",
        type: "policy",
        title: "LEGO Replay Program",
        embedding_status: "indexed",
        updated_at: "2024-12-17T10:00:00Z",
      },
      {
        id: "lego-k-3",
        type: "faq",
        title: "Is LEGO a good investment?",
        embedding_status: "indexed",
        updated_at: "2024-12-16T10:00:00Z",
      },
    ],
    healthScore: 94,
  };
}

function getLegoDemoKnowledge(): KnowledgeItem[] {
  return [
    // Materials
    {
      id: "lego-mat-1",
      type: "material",
      category: "Brick Materials",
      title: "ABS Plastic",
      summary:
        "Acrylonitrile Butadiene Styrene - the primary material for LEGO bricks since 1963. Known for durability, color retention, and precise clutch power.",
      source_type: "upload",
      embedding_status: "indexed",
      chunk_count: 4,
      is_published: 1,
      updated_at: "2024-12-15T10:00:00Z",
      tags: ["materials", "plastic", "manufacturing"],
    },
    {
      id: "lego-mat-2",
      type: "material",
      category: "Brick Materials",
      title: "Bio-PE Elements",
      summary:
        "Plant-based polyethylene made from sugarcane. Used for botanical elements like trees, leaves, and bushes. Part of LEGO sustainability initiative.",
      source_type: "upload",
      embedding_status: "indexed",
      chunk_count: 3,
      is_published: 1,
      updated_at: "2024-12-14T10:00:00Z",
      tags: ["materials", "sustainable", "bio-based"],
    },
    // Policies
    {
      id: "lego-pol-1",
      type: "policy",
      category: "Trade-In Program",
      title: "LEGO Replay Trade-In Guidelines",
      summary:
        "Accept gently used LEGO bricks and sets. Bricks are cleaned, sorted, and donated to children in need. Original boxes and instructions not required.",
      source_type: "upload",
      embedding_status: "indexed",
      chunk_count: 5,
      is_published: 1,
      updated_at: "2024-12-13T10:00:00Z",
      tags: ["trade-in", "replay", "donation"],
    },
    {
      id: "lego-pol-2",
      type: "policy",
      category: "Trade-In Program",
      title: "Brick Buyback Credit Rates",
      summary:
        "Store credit rates: Complete sets in good condition: 30-50% of retail. Bulk bricks: $8-12 per pound. Minifigures: Varies by rarity and condition.",
      source_type: "upload",
      embedding_status: "indexed",
      chunk_count: 4,
      is_published: 1,
      updated_at: "2024-12-12T10:00:00Z",
      tags: ["pricing", "credit", "buyback"],
    },
    // FAQs
    {
      id: "lego-faq-1",
      type: "faq",
      category: "Set Instructions",
      title: "How to identify genuine LEGO bricks?",
      summary:
        "Look for the LEGO logo on studs, check clutch power consistency, examine color uniformity, and verify precise dimensions (8mm stud width).",
      source_type: "manual",
      embedding_status: "indexed",
      chunk_count: 2,
      is_published: 1,
      updated_at: "2024-12-11T10:00:00Z",
      tags: ["authenticity", "identification", "quality"],
    },
    {
      id: "lego-faq-2",
      type: "faq",
      category: "Building Tips",
      title: "Best way to sort and organize LEGO bricks?",
      summary:
        "Recommended sorting methods: By color for display builders, by part type for MOC builders. Use stackable containers with compartments. Label everything.",
      source_type: "manual",
      embedding_status: "indexed",
      chunk_count: 3,
      is_published: 1,
      updated_at: "2024-12-10T10:00:00Z",
      tags: ["organization", "storage", "tips"],
    },
    {
      id: "lego-faq-3",
      type: "faq",
      category: "Set Instructions",
      title: "Can I get replacement parts for my set?",
      summary:
        "Yes! LEGO offers free replacement parts through LEGO.com/service. Identify parts using the element ID on the back of instructions or Bricks & Pieces.",
      source_type: "manual",
      embedding_status: "indexed",
      chunk_count: 2,
      is_published: 1,
      updated_at: "2024-12-09T10:00:00Z",
      tags: ["replacement", "parts", "service"],
    },
    // Products
    {
      id: "lego-prod-1",
      type: "product",
      category: "Products",
      title: "Technic Porsche 911 GT3 RS (42056)",
      summary:
        "Highly detailed 1:8 scale Porsche model with 2,704 pieces. Features working gearbox, steering, and suspension. Retired set, highly collectible.",
      source_type: "sync",
      embedding_status: "indexed",
      chunk_count: 6,
      is_published: 1,
      updated_at: "2024-12-18T10:00:00Z",
      tags: ["technic", "porsche", "supercar"],
    },
    {
      id: "lego-prod-2",
      type: "product",
      category: "Products",
      title: "Star Wars Millennium Falcon UCS (75192)",
      summary:
        "Ultimate Collector Series Millennium Falcon with 7,541 pieces. Includes display stand and fact plaque. Largest LEGO set ever produced.",
      source_type: "sync",
      embedding_status: "indexed",
      chunk_count: 8,
      is_published: 1,
      updated_at: "2024-12-17T10:00:00Z",
      tags: ["star-wars", "ucs", "millennium-falcon"],
    },
    // Training
    {
      id: "lego-train-1",
      type: "training",
      category: "Building Tips",
      title: "Advanced Building Techniques",
      summary:
        "SNOT (Studs Not On Top), sideways building, and illegal techniques explained. Learn how master builders create smooth surfaces and organic shapes.",
      source_type: "upload",
      embedding_status: "indexed",
      chunk_count: 12,
      is_published: 1,
      updated_at: "2024-12-08T10:00:00Z",
      tags: ["techniques", "advanced", "building"],
    },
  ];
}

function getNikeDemoStats(): KnowledgeStats {
  return {
    total: 42,
    indexed: 40,
    pending: 2,
    errors: 0,
    chunks: 285,
    countsByType: {
      product: 15,
      material: 8,
      policy: 6,
      faq: 10,
      training: 3,
      custom: 0,
    },
    countsByStatus: {
      indexed: 40,
      pending: 2,
      processing: 0,
      error: 0,
    },
    categories: [
      "Footwear",
      "Trade-In Program",
      "Materials & Technology",
      "Sizing & Fit",
      "Care Instructions",
    ],
    recentItems: [
      {
        id: "nike-k-1",
        type: "policy",
        title: "Nike Refurbished Trade-In Policy",
        embedding_status: "indexed",
        updated_at: "2024-12-18T10:00:00Z",
      },
      {
        id: "nike-k-2",
        type: "material",
        title: "Nike Air Technology Guide",
        embedding_status: "indexed",
        updated_at: "2024-12-17T10:00:00Z",
      },
      {
        id: "nike-k-3",
        type: "faq",
        title: "Sneaker Condition Grading FAQ",
        embedding_status: "indexed",
        updated_at: "2024-12-16T10:00:00Z",
      },
    ],
    healthScore: 95,
  };
}

function getNikeDemoKnowledge(): KnowledgeItem[] {
  return [
    // Materials
    {
      id: "nike-mat-1",
      type: "material",
      category: "Technology",
      title: "Nike Air Technology",
      summary:
        "Nike Air cushioning technology uses pressurized air inside tough yet flexible bags. Available in Air Max, Air Zoom, and Air VaporMax variants for different performance needs.",
      source_type: "sync",
      embedding_status: "indexed",
      chunk_count: 8,
      is_published: 1,
      updated_at: "2024-12-18T10:00:00Z",
      tags: ["air", "cushioning", "technology"],
    },
    {
      id: "nike-mat-2",
      type: "material",
      category: "Technology",
      title: "Nike React Foam",
      summary:
        "Nike React foam delivers exceptional energy return and cushioning. Lightweight, durable, and responsive for running and lifestyle footwear.",
      source_type: "sync",
      embedding_status: "indexed",
      chunk_count: 6,
      is_published: 1,
      updated_at: "2024-12-17T10:00:00Z",
      tags: ["react", "foam", "cushioning"],
    },
    {
      id: "nike-mat-3",
      type: "material",
      category: "Technology",
      title: "Flyknit Construction",
      summary:
        "Nike Flyknit uses high-strength fibers precisely engineered for lightweight support, breathability, and flexibility. Reduces waste in production.",
      source_type: "sync",
      embedding_status: "indexed",
      chunk_count: 7,
      is_published: 1,
      updated_at: "2024-12-16T10:00:00Z",
      tags: ["flyknit", "upper", "sustainability"],
    },
    // Policies
    {
      id: "nike-pol-1",
      type: "policy",
      category: "Trade-In Program",
      title: "Nike Refurbished Trade-In Policy",
      summary:
        "Trade in gently used Nike and Jordan footwear for store credit. Shoes must be authentic, clean, and in wearable condition. Credit values: Excellent 45%, Good 30%, Fair 15%.",
      source_type: "upload",
      embedding_status: "indexed",
      chunk_count: 5,
      is_published: 1,
      updated_at: "2024-12-18T10:00:00Z",
      tags: ["trade-in", "refurbished", "credit"],
    },
    {
      id: "nike-pol-2",
      type: "policy",
      category: "Trade-In Program",
      title: "Condition Grading Standards",
      summary:
        "Footwear grading criteria: Excellent (minimal wear, clean), Good (light wear, minor scuffs), Fair (moderate wear, visible use), Poor (heavy wear, not eligible).",
      source_type: "upload",
      embedding_status: "indexed",
      chunk_count: 4,
      is_published: 1,
      updated_at: "2024-12-17T10:00:00Z",
      tags: ["grading", "condition", "standards"],
    },
    // FAQs
    {
      id: "nike-faq-1",
      type: "faq",
      category: "Sizing & Fit",
      title: "Nike Sizing Guide",
      summary:
        "Nike shoes typically fit true to size. Running shoes may fit slightly narrow; lifestyle shoes like Air Force 1 run half size large. Always measure both feet.",
      source_type: "upload",
      embedding_status: "indexed",
      chunk_count: 6,
      is_published: 1,
      updated_at: "2024-12-15T10:00:00Z",
      tags: ["sizing", "fit", "guide"],
    },
    {
      id: "nike-faq-2",
      type: "faq",
      category: "Trade-In Program",
      title: "Trade-In FAQ",
      summary:
        "Common questions about Nike Refurbished: What brands accepted? Nike and Jordan. Where to trade in? Any participating Nike store. How long for credit? Instant at drop-off.",
      source_type: "upload",
      embedding_status: "indexed",
      chunk_count: 5,
      is_published: 1,
      updated_at: "2024-12-14T10:00:00Z",
      tags: ["trade-in", "faq", "questions"],
    },
    // Training
    {
      id: "nike-train-1",
      type: "training",
      category: "Product Knowledge",
      title: "Nike Running Shoe Selection Guide",
      summary:
        "Help customers choose between Pegasus (daily trainer), Vomero (cushioned), Structure (stability), and Alphafly (racing). Match to running style and goals.",
      source_type: "upload",
      embedding_status: "indexed",
      chunk_count: 10,
      is_published: 1,
      updated_at: "2024-12-12T10:00:00Z",
      tags: ["running", "selection", "training"],
    },
    {
      id: "nike-train-2",
      type: "training",
      category: "Trade-In Process",
      title: "Condition Assessment Training",
      summary:
        "Staff guide for assessing trade-in footwear condition. Check sole wear, upper integrity, cleanliness, and authenticity markers. Use grading rubric for consistent valuations.",
      source_type: "upload",
      embedding_status: "indexed",
      chunk_count: 8,
      is_published: 1,
      updated_at: "2024-12-10T10:00:00Z",
      tags: ["assessment", "grading", "training"],
    },
  ];
}

function getTenantDemoStats(tenantId: string): KnowledgeStats {
  switch (tenantId) {
    case "arcteryx":
      return getArcteryxDemoStats();
    case "lululemon":
      return getLululemonDemoStats();
    case "nespresso":
      return getNespressoDemoStats();
    case "lego":
      return getLegoDemoStats();
    case "nike":
      return getNikeDemoStats();
    default:
      return getLululemonDemoStats();
  }
}

function getTenantDemoKnowledge(tenantId: string): KnowledgeItem[] {
  switch (tenantId) {
    case "arcteryx":
      return getArcteryxDemoKnowledge();
    case "lululemon":
      return getLululemonDemoKnowledge();
    case "nespresso":
      return getNespressoDemoKnowledge();
    case "lego":
      return getLegoDemoKnowledge();
    case "nike":
      return getNikeDemoKnowledge();
    default:
      return getLululemonDemoKnowledge();
  }
}
